// ============================================================================
// Settings (spec §14.2, §7.4) — learner display preferences persisted on the
// profile: display name, romanization on/off (default off — it's a crutch),
// on-screen Hangul keyboard on/off, and a daily study goal. Theme lives in the
// app bar. Writes go through updateMyProfile (column-level grants allow only
// these preference columns; role/status are locked).
// ============================================================================

import { el, mount, toast } from '../dom.js';
import { getState, setState } from '../state/store.js';
import * as api from '@core/data/api.js';

export function renderSettings({ navigate }) {
  const container = el('div.container.stack');

  async function save(patch, { silent = false } = {}) {
    try {
      const updated = await api.updateMyProfile(patch);
      setState({ profile: updated });
      if (!silent) toast('Saved', 'success');
    } catch (e) {
      toast(e.message || 'Could not save', 'error');
    }
    render();
  }

  function toggleRow({ label, help, checked, onchange }) {
    const input = el('input', { type: 'checkbox', checked, onchange: (e) => onchange(e.target.checked) });
    return el('label.row.settings-row', { style: 'align-items:flex-start;gap:var(--space-3);cursor:pointer' }, [
      input,
      el('div.stack', { style: 'gap:2px' }, [
        el('strong', { text: label }),
        help ? el('div.faint', { text: help }) : false,
      ]),
    ]);
  }

  function render() {
    const p = getState().profile || {};
    const romanization = !!p.show_romanization;
    const keyboard = p.show_onscreen_keyboard == null ? true : !!p.show_onscreen_keyboard;

    const name = el('input.input', { type: 'text', value: p.display_name || '', maxlength: 60 });
    const nameSave = el('button.btn', {
      text: 'Save', onclick: () => {
        const v = name.value.trim();
        if (v && v !== p.display_name) save({ display_name: v });
      },
    });

    const goal = el('input.input', { type: 'number', min: '0', max: '500', value: p.daily_goal ?? '', style: 'max-width:8rem', placeholder: 'e.g. 20' });
    const goalSave = el('button.btn', {
      text: 'Save', onclick: () => {
        const v = goal.value === '' ? null : Math.max(0, Math.min(500, Number(goal.value) || 0));
        save({ daily_goal: v });
      },
    });

    mount(container,
      el('div.row', {}, [el('button.btn.btn--ghost', { text: '← Home', onclick: () => navigate('#/') }), el('div.appbar__spacer')]),
      el('div.card.stack', {}, [el('h1', { text: 'Settings' }), el('p.muted', { text: 'Preferences are saved to your account and apply everywhere.' })]),

      el('div.card.stack', {}, [
        el('h2', { text: 'Display name' }),
        el('div.row', { style: 'gap:var(--space-2)' }, [name, nameSave]),
      ]),

      el('div.card.stack', {}, [
        el('h2', { text: 'Study display' }),
        toggleRow({
          label: 'Show romanization',
          help: 'Display Revised Romanization (e.g. 학 → “hak”) alongside readings and words. Off by default — leaning on it slows real reading.',
          checked: romanization,
          onchange: (v) => save({ show_romanization: v }),
        }),
        toggleRow({
          label: 'Show on-screen Hangul keyboard',
          help: 'In typing drills, show the 두벌식 on-screen keyboard. Turn off if you type Hangul with your own keyboard.',
          checked: keyboard,
          onchange: (v) => save({ show_onscreen_keyboard: v }),
        }),
      ]),

      el('div.card.stack', {}, [
        el('h2', { text: 'Daily goal' }),
        el('p.faint', { text: 'Target number of reviews per day (used for streaks and progress). Leave blank for no goal.' }),
        el('div.row', { style: 'gap:var(--space-2)' }, [goal, goalSave]),
      ]),
    );
  }

  render();
  return container;
}
